import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, Calendar, DollarSign, Download, Edit3, FileText, Plus, RefreshCw, Save, Search, Trash2, Users, X } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { clientService } from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { exportCsv } from '../utils/csv';

const emptyForm = {
  name: '',
  document: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  status: 'active',
  plan_name: '',
  monthly_value: '',
  billing_day: '',
  contract_start: '',
  contract_end: '',
  notes: '',
};

const statusLabels = {
  active: 'Ativo',
  overdue: 'Em atraso',
  suspended: 'Suspenso',
  cancelled: 'Cancelado',
};

const statusClass = {
  active: 'badge-success',
  overdue: 'badge-warning',
  suspended: 'badge-danger',
  cancelled: 'badge-info',
};

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const dateLabel = (value) => {
  if (!value) return '-';
  return format(new Date(`${value}T00:00:00`), 'dd/MM/yyyy');
};

const toForm = (client) => ({
  name: client.name || '',
  document: client.document || '',
  email: client.email || '',
  phone: client.phone || '',
  address: client.address || '',
  city: client.city || '',
  state: client.state || '',
  status: client.status || 'active',
  plan_name: client.plan_name || '',
  monthly_value: client.monthly_value || '',
  billing_day: client.billing_day || '',
  contract_start: client.contract_start || '',
  contract_end: client.contract_end || '',
  notes: client.notes || '',
});

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);

    try {
      const response = await clientService.getAll();
      setClients(response.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();

    return clients.filter((client) => {
      const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
      const matchesSearch = !term || [
        client.name,
        client.document,
        client.email,
        client.phone,
        client.city,
        client.plan_name,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term));

      return matchesStatus && matchesSearch;
    });
  }, [clients, search, statusFilter]);

  const monthlyTotal = clients
    .filter((client) => client.status === 'active')
    .reduce((sum, client) => sum + Number(client.monthly_value || 0), 0);
  const activeCount = clients.filter((client) => client.status === 'active').length;
  const overdueCount = clients.filter((client) => client.status === 'overdue').length;

  const openCreateModal = () => {
    setEditingClient(null);
    setFormData(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setFormData(toForm(client));
    setModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const buildPayload = () => ({
    ...formData,
    monthly_value: formData.monthly_value === '' ? 0 : Number(formData.monthly_value),
    billing_day: formData.billing_day === '' ? null : Number(formData.billing_day),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = buildPayload();

      if (editingClient) {
        const response = await clientService.update(editingClient.id, payload);
        setClients((current) => current.map((client) => (
          client.id === editingClient.id ? response.data.data : client
        )));
        toast.success('Cliente atualizado com sucesso');
      } else {
        const response = await clientService.create(payload);
        setClients((current) => [response.data.data, ...current]);
        toast.success('Cliente criado com sucesso');
      }

      setModalOpen(false);
      setEditingClient(null);
      setFormData(emptyForm);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    const confirmed = window.confirm(`Remover ${client.name}?`);
    if (!confirmed) return;

    try {
      await clientService.delete(client.id);
      setClients((current) => current.filter((item) => item.id !== client.id));
      toast.success('Cliente removido');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover cliente');
    }
  };

  const handleExport = () => {
    exportCsv('rcrastro-clientes.csv', [
      { key: 'name', label: 'Nome' },
      { key: 'document', label: 'Documento' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Telefone' },
      { key: 'city', label: 'Cidade' },
      { key: 'status', label: 'Status' },
      { key: 'plan_name', label: 'Plano' },
      { key: 'monthly_value', label: 'Mensalidade' },
      { key: 'billing_day', label: 'Vencimento' },
      { key: 'contract_start', label: 'Inicio contrato' },
      { key: 'contract_end', label: 'Fim contrato' },
    ], filteredClients.map((client) => ({
      ...client,
      status: statusLabels[client.status] || client.status,
      monthly_value: currency.format(Number(client.monthly_value || 0)),
      contract_start: dateLabel(client.contract_start),
      contract_end: dateLabel(client.contract_end),
    })));
    toast.success('CSV de clientes gerado');
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Clientes e contratos</h1>
              <p className="text-dark-400">Gerencie clientes, planos, mensalidades e vencimentos.</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleExport}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Exportar CSV
              </button>
              <button
                type="button"
                onClick={loadClients}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Atualizar
              </button>
              <button
                type="button"
                onClick={openCreateModal}
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Novo cliente
              </button>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Users className="h-4 w-4" />
                Total
              </div>
              <p className="text-3xl font-bold text-white">{clients.length}</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Briefcase className="h-4 w-4" />
                Ativos
              </div>
              <p className="text-3xl font-bold text-success-400">{activeCount}</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Calendar className="h-4 w-4" />
                Em atraso
              </div>
              <p className="text-3xl font-bold text-warning-400">{overdueCount}</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <DollarSign className="h-4 w-4" />
                Receita mensal
              </div>
              <p className="text-3xl font-bold text-primary-400">{currency.format(monthlyTotal)}</p>
            </div>
          </div>

          <div className="card mb-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_220px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-dark-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="input-field pl-11"
                  placeholder="Buscar por cliente, documento, email, cidade ou plano"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="input-field"
              >
                <option value="all">Todos os status</option>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="card p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px] text-left">
                <thead className="border-b border-dark-700 text-sm text-dark-400">
                  <tr>
                    <th className="px-5 py-4 font-medium">Cliente</th>
                    <th className="px-5 py-4 font-medium">Contato</th>
                    <th className="px-5 py-4 font-medium">Contrato</th>
                    <th className="px-5 py-4 font-medium">Mensalidade</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 text-right font-medium">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-dark-400">
                        Carregando clientes...
                      </td>
                    </tr>
                  ) : filteredClients.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-dark-400">
                        <FileText className="mx-auto mb-2 h-10 w-10 opacity-50" />
                        Nenhum cliente encontrado
                      </td>
                    </tr>
                  ) : (
                    filteredClients.map((client) => (
                      <tr key={client.id} className="border-b border-dark-800 text-sm last:border-0">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-white">{client.name}</div>
                          <div className="text-dark-400">{client.document || 'Documento nao informado'}</div>
                          <div className="text-xs text-dark-500">
                            {[client.city, client.state].filter(Boolean).join(' - ')}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-dark-300">
                          <div>{client.email || 'Sem email'}</div>
                          <div className="text-xs text-dark-500">{client.phone || 'Sem telefone'}</div>
                        </td>
                        <td className="px-5 py-4 text-dark-300">
                          <div className="font-medium text-white">{client.plan_name || 'Sem plano'}</div>
                          <div className="text-xs text-dark-500">
                            {dateLabel(client.contract_start)} ate {dateLabel(client.contract_end)}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-dark-300">
                          <div className="font-semibold text-white">
                            {currency.format(Number(client.monthly_value || 0))}
                          </div>
                          <div className="text-xs text-dark-500">
                            Vence dia {client.billing_day || '-'}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`badge ${statusClass[client.status] || 'badge-info'}`}>
                            {statusLabels[client.status] || client.status}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(client)}
                              className="rounded-lg p-2 text-primary-400 transition-colors hover:bg-dark-700"
                              title="Editar"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(client)}
                              className="rounded-lg p-2 text-danger-400 transition-colors hover:bg-dark-700"
                              title="Remover"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg border border-dark-700 bg-dark-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-dark-800 p-5">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {editingClient ? 'Editar cliente' : 'Novo cliente'}
                </h2>
                <p className="text-sm text-dark-400">Dados cadastrais e comerciais do contrato.</p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-2 text-dark-400 transition-colors hover:bg-dark-800 hover:text-white"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Nome</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Nome do cliente"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Documento</label>
                  <input
                    name="document"
                    value={formData.document}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="CPF ou CNPJ"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="email@cliente.com"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Telefone</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Endereco</label>
                  <input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Rua, numero, bairro"
                  />
                </div>

                <div className="grid grid-cols-[1fr_90px] gap-3">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-dark-300">Cidade</label>
                    <input
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Cidade"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-dark-300">UF</label>
                    <input
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="input-field uppercase"
                      placeholder="SP"
                      maxLength="2"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Plano</label>
                  <input
                    name="plan_name"
                    value={formData.plan_name}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Essencial, Pro, Frota..."
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="input-field"
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Mensalidade</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    name="monthly_value"
                    value={formData.monthly_value}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="149.90"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Dia de vencimento</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    name="billing_day"
                    value={formData.billing_day}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="10"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Inicio do contrato</label>
                  <input
                    type="date"
                    name="contract_start"
                    value={formData.contract_start}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Fim do contrato</label>
                  <input
                    type="date"
                    name="contract_end"
                    value={formData.contract_end}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">Observacoes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="input-field min-h-24"
                  placeholder="Informacoes internas do cliente ou contrato"
                />
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-dark-800 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Salvando...' : 'Salvar cliente'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
